import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { Clubs, Jobs } from '../public/signup/club/club.model';
import { Teams } from './club/club.model';
import { CONST } from './const';
import { Address } from './profile/profile.model';
import { UtilitiesService } from './utilities.provider';

export interface UserInfo {
    address?: Address,
    birthDate?: Date,
    club: Clubs,
    sports: {
        id: number,
        desc: string,
        points?: number,
        club?: {
            id: number,
            certified: boolean,
            name: string,
            address: any,
            workers: any,
            athletesRange: any,
            picture: string
        },
        team?: Teams,
    }[],
    email?: string,
    firstName?: string,
    gender?: string,
    hasBirthCert?: boolean,
    heightFeet?: number,
    heightInches?: number,
    id: number,
    lastLogin?: Date,
    lastName?: string,
    name?: string
    parent?: any,
    phone: string,
    profilePic?: string,
    role: string,
    roles?: any[],
    status: string,
    step: number,
    parqp: number,
    username?: string,
    weight?: number,
    jobTitle?: {
        id: number,
        value: string
    },
    staff?: boolean,
    coach?: boolean,
}

@Injectable()
export class AuthService {

    public last_page: any = null;

    constructor(private http: HttpClient, private router: Router,
    private sanitizer: DomSanitizer) {}

    login(user: string, password: string) {
        const body = {};
        return this.http.post(environment.urlApi + '/users/login', body,
            {
                headers: new HttpHeaders({ 'Authorization': 'Basic ' + btoa(user + ':' + password) }),
                observe: 'response'
            });

    }

    getProfileData(role: string) {
        return this.http.post(environment.urlApi + '/users/login?to=' + role, {});
    }

    getUserRoles(userId) {
        return this.http.get(environment.urlApi + '/users/' + userId + '/roles');
    }

    getUserRolesStatus(userId) {
        return this.http.get(environment.urlApi + '/users/' + userId + '/roles/status');
    }

    logout(is401?: boolean) {
        return this.http.delete(environment.urlApi + '/users/logout').toPromise();;
    }

    setMainRole(userId: number, role: string) {
        return this.http.put(environment.urlApi + '/users/' + userId + '/roles/' + role + '/default', {});
    }

    getRolesStatus(userId: number) {
        return this.http.get(environment.urlApi + '/users/' + userId + '/roles/status');
    }

    addSecondaryRole(userId: number, role: string, club?: Clubs, jobTitle?: Jobs) {
        let data: any = {
            role: role
        }
        if (club) {
            data.sports = [{club: club}];
        }
        if (jobTitle) {
            data.jobTitle = jobTitle
        }
        return this.http.post(environment.urlApi + '/users/' + userId + '/roles', data);
    }

    changePassword(data: any) {
        return this.http.post(environment.urlApi + '/users/password', data);
    }

    public setDataSession(userinfo: UserInfo, token?: string, email?: string, remember?: boolean) {
        if (token) {
            let tokenEncrypted = UtilitiesService.encrypt(token);
            sessionStorage.setItem(CONST.LOCALSTORAGE.token, tokenEncrypted);
        }
        if (remember) {
            localStorage.setItem(CONST.LOCALSTORAGE.rememberMe, "true");
        } else {
            localStorage.removeItem(CONST.LOCALSTORAGE.rememberMe);
        }

        let userDataEncrypted = UtilitiesService.encrypt(userinfo);
        sessionStorage.setItem(CONST.LOCALSTORAGE.user, userDataEncrypted);
        email ? localStorage.setItem(CONST.LOCALSTORAGE.email, email) : null;
    }

    public clearSession() {
        let isRememberme = localStorage.getItem(CONST.LOCALSTORAGE.rememberMe);
        sessionStorage.removeItem(CONST.LOCALSTORAGE.token);
        sessionStorage.removeItem(CONST.LOCALSTORAGE.user);
        if (!isRememberme || isRememberme == 'false') {
            localStorage.removeItem(CONST.LOCALSTORAGE.email);
        }
    }

    public getUserInfo(notInvalidate?: boolean): UserInfo | null {
        let encrypterUser = sessionStorage.getItem(CONST.LOCALSTORAGE.user);
        if (encrypterUser) {
            let decryptedUser = UtilitiesService.decrypt(encrypterUser);

            if (decryptedUser && decryptedUser.validator == CONST.CRYPTO.validator) {
                return decryptedUser.data;
            } else {
                !notInvalidate ? this.invalidateSession() : null;
                return null;
            }
        } else {
            !notInvalidate ? this.invalidateSession() : null;
            return null;
        }
    }

    async getIdentificationDocumentPic(idAthlete?: number) {
        if (idAthlete) {
            return await this.http.get(environment.urlApi.concat(`/athletes/${idAthlete}/doc-img`), { responseType: 'blob' as 'json' }).toPromise();
        } else {
            return await this.http.get(environment.urlApi.concat('/users/profile/doc-img'), { responseType: 'blob' as 'json' }).toPromise();
        }
    }

    public getToken(): string | null {
        let encrypterToken = sessionStorage.getItem(CONST.LOCALSTORAGE.token);
        if (encrypterToken) {
            let decryptedToken = UtilitiesService.decrypt(encrypterToken);
            if (decryptedToken && decryptedToken.validator == CONST.CRYPTO.validator) {
                return decryptedToken.data;
            } else {
                return null;
            }
        } else {
            return null;
        }
    }

    private invalidateSession() {
        this.clearSession();
        localStorage.setItem(CONST.LOCALSTORAGE.errorLogin, 'Session Invalidated.');
        this.router.navigateByUrl('/public/home', { replaceUrl: true })

    }

    public convertBlobToBase64(blob: any) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader;
            reader.onerror = reject;
            reader.onload = () => {
                let url = this.sanitizer.bypassSecurityTrustUrl(reader.result.toString());
                resolve(url);
            };
            reader.readAsDataURL(blob);
        });
    }
    
    validateTokenRestorePassword(uuid) {
      let params = new HttpParams();
      params = params.set('uuid', uuid.toString());
      return this.http.get(environment.urlApi + '/users/restore/password', {params: params}).toPromise();
    }

    tokenRestorePassword(body, uuid?) {
      let params = new HttpParams();
      if(uuid) {
        params = params.set('uuid', uuid.toString());
      }
      return this.http.put(environment.urlApi + '/users/restore/password', body, {params: params}).toPromise();
    }

}